// path: frontend/src/components/layout/Layout.tsx
import { Suspense, useLayoutEffect, useRef } from 'react';

import { Outlet, useLocation } from 'react-router-dom';

import { PublicAnalyticsTracker } from '@/components/analytics/PublicAnalyticsTracker';
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary';

import Footer from './Footer';
import Header from './HeaderNew';

function shouldPreserveAnchoredScroll(location: { search: string; hash: string }) {
  const searchParams = new URLSearchParams(location.search);
  return Boolean(location.hash || searchParams.get('section') || searchParams.get('focus'));
}

function ScrollToTopOnPathChange() {
  const location = useLocation();
  const { hash, pathname, search } = location;
  const previousPathRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (typeof globalThis === 'undefined' || !globalThis.window) return undefined;

    const browserWindow = globalThis.window;
    const previousMode = browserWindow.history.scrollRestoration;
    browserWindow.history.scrollRestoration = 'manual';

    return () => {
      browserWindow.history.scrollRestoration = previousMode;
    };
  }, []);

  useLayoutEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = pathname;

    const isInitialLoad = previousPath === null;
    const pathnameChanged = previousPath !== pathname;

    if ((!isInitialLoad && !pathnameChanged) || shouldPreserveAnchoredScroll({ hash, search })) {
      return;
    }

    const browserWindow = typeof globalThis !== 'undefined' ? globalThis.window : null;
    const browserDocument = typeof globalThis !== 'undefined' ? globalThis.document : null;
    if (!browserWindow) return;

    const resetScroll = () => {
      browserWindow.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      browserDocument?.documentElement?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
      browserDocument?.body?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
    };

    const frame = browserWindow.requestAnimationFrame(resetScroll);
    return () => browserWindow.cancelAnimationFrame(frame);
  }, [hash, pathname, search]);

  return null;
}

function LayoutRouteFallback() {
  return (
    <div className="flex min-h-[48vh] items-center justify-center">
      <div className="flex flex-col items-center gap-4 text-center text-[#5f5346]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#8b5e3c] border-t-transparent" />
        <p className="text-sm font-semibold">Loading…</p>
      </div>
    </div>
  );
}

export const Layout = () => {
  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTopOnPathChange />
      <PublicAnalyticsTracker />
      <Header />

      <main className="flex-1 pt-(--header-height)">
        <AppErrorBoundary>
          <Suspense fallback={<LayoutRouteFallback />}>
            <Outlet />
          </Suspense>
        </AppErrorBoundary>
      </main>

      <Footer />
    </div>
  );
};
